import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Loader2, Mail, Instagram } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    budget: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.', {
        description: 'Your message has been sent successfully.',
      });
      setFormData({ name: '', email: '', phone: '', budget: '', message: '' });
    } catch (error) {
      toast.error('Oops! Something went wrong.', {
        description: 'Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #050508 0%, #0a0a15 50%, #050508 100%)',
      }}
      data-testid="cta-section"
    >
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-[600px] h-[400px] bg-[#7B2EFF]/8 rounded-full blur-[150px]" />
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-[#00D4FF]/5 rounded-full blur-[120px]" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block px-4 py-2 rounded-full text-xs uppercase tracking-[0.2em] text-[#7B2EFF] bg-[#7B2EFF]/10 border border-[#7B2EFF]/20 backdrop-blur-sm mb-6">
              Get Started
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              Let's craft your next{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7B2EFF] to-[#00D4FF]">
                web advantage
              </span>.
            </h2>
            <p className="mt-6 text-white/50 text-lg leading-relaxed">
              Tell us about your project timeline, budget, and goals. We'll get back to you within 24 hours with a custom proposal.
            </p>

            {/* Contact Info Cards - Glass Style */}
            <div className="mt-10 space-y-4">
              <motion.div 
                className="flex items-center gap-4 p-4 rounded-2xl glass-card"
                whileHover={{ scale: 1.02 }}
              >
                <div className="w-12 h-12 rounded-xl grid place-items-center bg-[#7B2EFF]/10 border border-[#7B2EFF]/30">
                  <Mail className="w-5 h-5 text-[#7B2EFF]" />
                </div>
                <div>
                  <p className="text-white/40 text-sm">Email us at</p>
                  <p className="text-white font-medium">contact@keisya.id</p>
                </div>
              </motion.div>
              <motion.a 
                href="https://instagram.com/personalweb_builder" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-2xl glass-card group block"
                whileHover={{ scale: 1.02 }}
              >
                <div className="w-12 h-12 rounded-xl grid place-items-center bg-gradient-to-br from-[#7B2EFF]/20 to-[#E1306C]/20 border border-[#7B2EFF]/20 group-hover:from-[#7B2EFF]/30 group-hover:to-[#E1306C]/30 transition-all">
                  <Instagram className="w-5 h-5 text-[#E1306C]" />
                </div>
                <div>
                  <p className="text-white/40 text-sm">Follow us on Instagram</p>
                  <p className="text-white font-medium group-hover:text-[#7B2EFF] transition-colors">@personalweb_builder</p>
                </div>
              </motion.a>
            </div>

            {/* Trust Badges - Glass Pills */}
            <div className="mt-8 flex flex-wrap gap-3">
              {['Fast Response', 'NDA Available', 'Flexible Budget'].map((badge) => (
                <span
                  key={badge}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm text-white/60 glass-card"
                >
                  <CheckCircle className="w-4 h-4 text-[#22C55E]" />
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Form - Floating Glass Panel */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="glass-panel rounded-3xl p-6 md:p-8"
          >
            {isSubmitted ? (
              <div className="text-center py-12">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/30 grid place-items-center mx-auto mb-6"
                >
                  <CheckCircle className="w-10 h-10 text-[#22C55E]" />
                </motion.div>
                <h3 className="text-2xl font-semibold text-white mb-2">Message Sent!</h3>
                <p className="text-white/50">We'll be in touch within 24 hours.</p>
                <motion.button
                  onClick={() => setIsSubmitted(false)}
                  className="mt-8 px-6 py-3 rounded-xl font-medium glass-card text-white"
                  whileHover={{ scale: 1.02 }}
                  data-testid="send-another-button"
                >
                  Send Another Message
                </motion.button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="text-white/60 text-sm mb-2 block font-medium">
                    Name <span className="text-[#7B2EFF]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className={`w-full bg-white/5 border ${errors.name ? 'border-red-400' : 'border-white/10'} rounded-xl px-4 py-3.5 text-white placeholder:text-white/25 focus:outline-none focus:border-[#7B2EFF]/60 focus:bg-white/[0.07] transition-all backdrop-blur-sm`}
                    data-testid="contact-form-name-input"
                  />
                  {errors.name && (
                    <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="text-white/60 text-sm mb-2 block font-medium">
                    Email <span className="text-[#7B2EFF]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className={`w-full bg-white/5 border ${errors.email ? 'border-red-400' : 'border-white/10'} rounded-xl px-4 py-3.5 text-white placeholder:text-white/25 focus:outline-none focus:border-[#7B2EFF]/60 focus:bg-white/[0.07] transition-all backdrop-blur-sm`}
                    data-testid="contact-form-email-input"
                  />
                  {errors.email && (
                    <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="text-white/60 text-sm mb-2 block font-medium">
                    Phone <span className="text-white/30">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/25 focus:outline-none focus:border-[#7B2EFF]/60 focus:bg-white/[0.07] transition-all backdrop-blur-sm"
                    data-testid="contact-form-phone-input"
                  />
                </div>

                {/* Budget */}
                <div>
                  <label htmlFor="budget" className="text-white/60 text-sm mb-2 block font-medium">
                    Budget <span className="text-white/30">(Optional)</span>
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-[#7B2EFF]/60 focus:bg-white/[0.07] transition-all backdrop-blur-sm appearance-none cursor-pointer"
                    data-testid="contact-form-budget-select"
                  >
                    <option value="" className="bg-[#0a0a12]">Select budget range</option>
                    <option value="Regular website: $200-$300" className="bg-[#0a0a12]">Regular website: $200 - $300</option>
                    <option value="Cool website: $300-$400" className="bg-[#0a0a12]">Cool website: $300 - $400</option>
                    <option value="Website with 3D animated visuals: $400-$500" className="bg-[#0a0a12]">Website with 3D animated visuals: $400 - $500</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="text-white/60 text-sm mb-2 block font-medium">
                    Message <span className="text-[#7B2EFF]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project..."
                    rows="4"
                    className={`w-full bg-white/5 border ${errors.message ? 'border-red-400' : 'border-white/10'} rounded-xl px-4 py-3.5 text-white placeholder:text-white/25 focus:outline-none focus:border-[#7B2EFF]/60 focus:bg-white/[0.07] transition-all backdrop-blur-sm resize-none`}
                    data-testid="contact-form-message-textarea"
                  />
                  {errors.message && (
                    <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 rounded-xl text-base font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed bg-gradient-to-r from-[#7B2EFF] to-[#5B1FCC] text-white shadow-lg shadow-[#7B2EFF]/25 mt-2"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 40px rgba(123, 46, 255, 0.4)' }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="contact-form-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Request</span>
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </motion.button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
